import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';

import { apiGetUser, apiResyncActurisConfig } from 'api';
import type { APIUserOverviewData } from 'types/api.types';
import {
  type ActurisSyncStatus,
  type ActurisSyncState,
  type ActurisConfigFields,
  type ActurisDisplayFields,
  type ActurisPersonnelOptions,
  getPollInterval,
  normalizeSyncStatus,
  getMissingFieldLabels,
} from 'hooks/acturis';

export interface UserActurisSyncData extends ActurisSyncState {
  editableFields: string[];
  personnelOptions: ActurisPersonnelOptions | null;
  config: ActurisConfigFields;
  display: ActurisDisplayFields;
}

export type { ActurisSyncStatus };

export const useUserActurisSyncStatus = (userId: number | undefined) => {
  const queryClient = useQueryClient();

  const {
    data: userData,
    isLoading,
    error,
    refetch,
  } = useQuery<APIUserOverviewData>({
    queryKey: ['user', userId?.toString()],
    queryFn: () => apiGetUser(userId!),
    enabled: !!userId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;

      return getPollInterval(data.acturis_sync_status, !!data.acturis_username);
    },
  });

  const resyncMutation = useMutation({
    mutationFn: () => apiResyncActurisConfig(userId!),
    onSuccess: () => {
      queryClient.setQueryData<APIUserOverviewData>(
        ['user', userId?.toString()],
        (old) =>
          old
            ? { ...old, acturis_sync_status: 'syncing', acturis_sync_error: '' }
            : old,
      );
    },
  });

  const resync = useCallback(() => {
    if (userId) {
      resyncMutation.mutate();
    }
  }, [userId, resyncMutation]);

  const hasUsername = !!userData?.acturis_username;
  const isConfigComplete = userData?.acturis_config_complete ?? false;
  const editableFields = userData?.acturis_editable_fields ?? [];

  const syncStatus = normalizeSyncStatus(
    userData?.acturis_sync_status,
    hasUsername,
    isConfigComplete,
  );

  const syncData: UserActurisSyncData = {
    syncStatus,
    syncError: userData?.acturis_sync_error || null,
    hasUsername,
    isConfigComplete,
    missingFields: getMissingFieldLabels(editableFields),
    canAccess: syncStatus === 'ready',
    editableFields,
    personnelOptions: userData?.acturis_personnel_options ?? null,
    config: {
      office: userData?.acturis_office || '',
      accountExecutive: userData?.acturis_account_executive || '',
      accountHandler: userData?.acturis_account_handler || '',
      team: userData?.acturis_team || '',
    },
    display: {
      office: userData?.acturis_office_display || null,
      executive: userData?.acturis_executive_display || null,
      handler: userData?.acturis_handler_display || null,
      team: userData?.acturis_team_display || null,
    },
  };

  return {
    userData,
    syncData,
    isLoading,
    error,
    refetch,
    resync,
    isResyncing: resyncMutation.isPending,
  };
};
